import { useMutation, useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import {
  disableNavigationGuard,
  useNavigationGuardBlocker,
} from '../../../hooks/useNavigationGuard';
import { InvoiceValidationEntry, NewInvoice } from '../../../types/invoices';
import { InvoiceFormValues, InvoiceTemplateSubPageContext } from './types';
import {
  Alert,
  FormControl,
  Autocomplete,
  TextField,
  Box,
  FormHelperText,
  Card,
} from '@mui/material';
import FileUploader from '../../../components/FileUploader';
import InputHeader from '../../../components/InputHeader';
import useContractSelectOptions from '../../../hooks/useContractSelectOptions';
import useForm from '../../../hooks/useForm';
import { mapFileToAppFile, AppFile } from '../../../types/core';
import getMaterialInputsFormHelper from '../../../utils/getMaterialInputsFormHelper';
import { getValidationErrorMessage, getMonthName } from './utils';
import { useCurrentUser } from '../../../contexts/currentUser';
import ConfirmationDialog, {
  ConsequencesList,
} from '../../../components/ConfirmationDialog';
import useApiErrorMessageGetter from '../../../hooks/useApiErrorMessage';
import { PANEL_CARD_INNER_SPACING_CLASSNAME } from '../../../components/AdminTemplates';

const InvoiceTabPage = () => {
  const { t } = useLanguage();
  const { userOperator, isOperator } = useCurrentUser();
  const { getContracts, createInvoice } = useIotsApi();
  const { enqueueSnackbar } = useSnackbar();
  const getErrorMessage = useApiErrorMessageGetter();
  const navigate = useNavigate();

  const [validationErrors, setValidationErrors] = useState<
    InvoiceValidationEntry[]
  >([]);

  const {
    invoice,
    invoiceId,
    invoiceInfo,
    onNewInvoiceFormStateChange,
    isFetching,
  } = useOutletContext<InvoiceTemplateSubPageContext>();

  const formElementRef = useRef<HTMLFormElement | null>(null);
  const newInvoiceFormStateChangeHandlerRef = useRef(
    onNewInvoiceFormStateChange,
  );
  newInvoiceFormStateChangeHandlerRef.current = onNewInvoiceFormStateChange;

  const [invoiceToCreate, setInvoiceToCreate] = useState<NewInvoice | null>(
    null,
  );

  const {
    mutate: applyCreateInvoiceMutation,
    isPending: isCreateInvoicePending,
    reset: resetCreateInvoiceMutation,
  } = useMutation({
    mutationFn: (newInvoice: NewInvoice) => createInvoice(newInvoice),
    onSuccess: data => {
      const { errors, invoice } = data;
      if (Array.isArray(errors) && errors.length > 0) {
        enqueueSnackbar({
          variant: 'error',
          message: t('content.invoice-creation-error'),
        });
        setValidationErrors(errors);
      } else if (invoice) {
        enqueueSnackbar({
          variant: 'success',
          message: t('content.invoice-creation-success'),
        });
        setValidationErrors([]);
        void disableNavigationGuard(() =>
          navigate(`/admin/invoices/${invoice.id}/invoice`),
        );
      }
    },
    onError: error => {
      enqueueSnackbar({
        variant: 'error',
        message: getErrorMessage(error, t('content.invoice-creation-error')),
      });
      resetCreateInvoiceMutation();
    },
  });

  const initialValues = useMemo<InvoiceFormValues>(() => {
    if (!invoice) {
      return {
        invoiceNumber: '',
        files: [],
      };
    }
    return {
      invoiceNumber: invoice.invoiceNumber,
      files: invoice.files.map(file => mapFileToAppFile(file)),
    };
  }, [invoice]);

  const { data: fetchedContracts = [] } = useQuery({
    queryKey: ['contracts'],
    queryFn: () => getContracts(),
    refetchOnMount: 'always',
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const useFormResult = useForm({
    initialValues,
    onSubmit: values => {
      if (!invoiceId) {
        const newInvoiceToCreate: NewInvoice = {
          invoiceNumber: values.invoiceNumber,
          year: invoiceInfo.year,
          month: invoiceInfo.month,
          contractId: invoiceInfo.contractId,
          files: values.files,
        };
        if (isOperator) {
          setInvoiceToCreate(newInvoiceToCreate);
        } else {
          applyCreateInvoiceMutation(newInvoiceToCreate);
        }
      }
    },
    validate: (values, setError) => {
      if (!values.invoiceNumber.trim()) {
        setError('invoiceNumber', t('validation.required-field'));
      }
      if (!values.files.length) {
        setError('files', t('validation.required-file'));
      }
    },
    resetOnInitialValuesChange: () => Boolean(invoice),
  });

  const {
    errors,
    handleSubmit,
    hasVisibleErrors,
    haveValuesChanged,
    values,
    updateValues,
  } = useFormResult;

  useNavigationGuardBlocker(() => haveValuesChanged);

  const areInputsDisabled =
    isFetching || isCreateInvoicePending || Boolean(invoice);

  const canBeSubmitted =
    !areInputsDisabled && haveValuesChanged && !hasVisibleErrors;

  /* The next effects implements a very dicey way to provide the means to allow a parent element to be in sync with the form state and trigger the submit event.
    
    This should be the only case where this kind of inversion of responsibilities is performed, and only supported because the page where this form lives is the sole exception where the "submit" button is displayed outside of the actual "form page" route. 
    
    If something like this is needed in more ocassions, a different page structure should be created so the form state is actually handled by the parent element/page */
  useEffect(() => {
    if (!invoiceId) {
      newInvoiceFormStateChangeHandlerRef.current(
        values.invoiceNumber,
        canBeSubmitted,
        () => formElementRef.current?.requestSubmit(),
      );
    }
  }, [values.invoiceNumber, canBeSubmitted, invoiceId]);

  useEffect(
    () => () => {
      // When the page is abandoned, the form state is completely reset
      newInvoiceFormStateChangeHandlerRef.current('', false, () => {});
    },
    [],
  );

  const { getTextFieldProps } = getMaterialInputsFormHelper(useFormResult, {
    shouldInputsBeDisabled: areInputsDisabled,
  });

  const { contractsMap } = useContractSelectOptions(
    fetchedContracts,
    userOperator,
  );

  return (
    <Card className={PANEL_CARD_INNER_SPACING_CLASSNAME}>
      {validationErrors.length > 0 && (
        <Alert severity="error" className="mb-4">
          <ul className="list-disc list-inside">
            {validationErrors.map((error, index) => (
              <li key={index}>{getValidationErrorMessage(error, t)}</li>
            ))}
          </ul>
        </Alert>
      )}
      <form onSubmit={handleSubmit} ref={formElementRef}>
        <div>
          <label className="block mb-4">
            <InputHeader required>{t('content.contract')}</InputHeader>
            <FormControl>
              <Autocomplete
                value={contractsMap[invoiceInfo.contractId] ?? null}
                onChange={() => {}}
                options={[]}
                disabled
                size="small"
                renderInput={params => <TextField {...params} />}
              />
            </FormControl>
          </label>
          <Box className="flex gap-4 items-start justify-between">
            <label className="block mb-4 w-full">
              <InputHeader required>{t('content.month')}</InputHeader>
              <TextField
                name="month"
                value={`${getMonthName(invoiceInfo.month - 1, t)} ${invoiceInfo.year}`}
                disabled
              />
            </label>
            <label className="block mb-4 w-full">
              <InputHeader required>{t('content.invoice-number')}</InputHeader>
              <TextField
                placeholder={t('content.invoice-number')}
                {...getTextFieldProps('invoiceNumber')}
              />
            </label>
          </Box>
          <div>
            <InputHeader required>{t('content.files')}</InputHeader>
            <span className="text-iots-grey-500 text-sm">
              {t('content.files-accepted-formats')}
            </span>
            <FormControl error={Boolean(errors.files)}>
              <FileUploader
                isError={Boolean(errors.files)}
                onChange={(validFiles: AppFile[]) => {
                  updateValues({
                    files: [...values.files, ...validFiles],
                  });
                }}
                onDelete={(id: string) => {
                  updateValues({
                    files: values.files.filter(file => file.id !== id),
                  });
                }}
                files={values.files}
                disabled={areInputsDisabled}
              />
              {errors.files && <FormHelperText>{errors.files}</FormHelperText>}
            </FormControl>
          </div>
        </div>
      </form>
      {!invoiceId && (
        <ConfirmationDialog
          open={Boolean(invoiceToCreate)}
          onConfirm={() => {
            if (invoiceToCreate) {
              applyCreateInvoiceMutation(invoiceToCreate);
              setInvoiceToCreate(null);
            }
          }}
          onClose={() => setInvoiceToCreate(null)}
          title={t('content.invoice-creation-confirmation.title')}
          confirmButtonText={t('content.i-understand')}
        >
          <ConsequencesList
            action={t('content.invoice-creation-confirmation.action')}
            list={[
              t(
                'content.invoice-creation-confirmation.bullet-points.ensure-annual-accounts-are-sent',
              ),
              t(
                'content.invoice-creation-confirmation.bullet-points.payment-requisites',
              ),
            ]}
          />
        </ConfirmationDialog>
      )}
    </Card>
  );
};

export default InvoiceTabPage;
